import React, { useEffect, useState } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { useSelector } from 'react-redux';
import {
  OperationVariables,
  QueryResult,
  useApolloClient
} from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Query } from '@apollo/client/react/components';
import { NewSearchFilters } from '../../NewSearchFilters';
import { NewSearchCities } from '../../NewSearchCities';
import { selectSearchFormResultByNumber } from '../../../../store/selectors';
import {
  CityAndLocationsQuery,
  SearchTotalCountQuery
} from '../../../../graphql/graphql';
import {
  cityAndLocationsQuery,
  searchTotalCountQuery
} from '../../../../api/queries';
import { NewSearchLocations } from '../../NewSearchLocations';
import { ModalParams, ModalTypeEnum } from '../types';
import { FormData } from '../../types';
import { Loader } from '../../../ui/Button/loader.styled';
import Bugsnag from '../../../../utils/bugsnag';

interface SearchBarModalsProps {
  handleHideWithUndo: () => void;
  hideFilters: () => void;
  formEl: React.MutableRefObject<{
    reset: (values: FormData) => void;
  } | null>;
  modalParams: ModalParams;
  searchVariables: {
    filter: {
      [key: string]: undefined | string | boolean | number;
    };
  };
}

export const SearchBarModals: React.FC<SearchBarModalsProps> = ({
  formEl,
  hideFilters,
  modalParams,
  handleHideWithUndo,
  searchVariables
}) => {
  const client = useApolloClient();
  const { values, submitting } = useFormState();
  const { submit } = useForm();
  const { t } = useTranslation();

  const resultByNumber = useSelector(selectSearchFormResultByNumber);
  const screen = modalParams?.initialScreen;
  const [citiesPopupIsOpen, setCitiesPopupIsOpen] = useState(
    modalParams?.initialScreen === ModalTypeEnum.City
  );
  const [locationsPopupIsOpen, setLocationsPopupIsOpen] = useState(
    modalParams?.initialScreen === ModalTypeEnum.Locations
  );

  useEffect(() => {
    setCitiesPopupIsOpen(modalParams?.initialScreen === ModalTypeEnum.City);
    setLocationsPopupIsOpen(
      modalParams?.initialScreen === ModalTypeEnum.Locations
    );
  }, [modalParams?.initialScreen]);

  const getCityLocations = async (cityId?: string) => {
    const locationsQueryVariables = {
      cityId,
      scope: 'ALL',
      limit: 1000
    };

    try {
      const { data } = await client.query<CityAndLocationsQuery>({
        query: cityAndLocationsQuery,
        variables: locationsQueryVariables
      });

      return data?.locations;
    } catch (error) {
      console.error(error);
      Bugsnag.notify(error);
    }
  };

  const closeCitiesHandler = async (wasChanged = true, cityId?: string) => {
    if (wasChanged) {
      const locations = await getCityLocations(cityId);

      if ((locations?.length || 0) > 0) {
        setLocationsPopupIsOpen(true);
      }

      if (
        !locations?.length &&
        (screen === ModalTypeEnum.Locations || screen === ModalTypeEnum.City)
      ) {
        hideFilters();
        setCitiesPopupIsOpen(false);
        submit();
      }
    }

    if (!wasChanged && screen !== ModalTypeEnum.Filters) {
      hideFilters();
    }

    setCitiesPopupIsOpen(false);
  };

  const openCitiesHandler = (event?: React.MouseEvent<HTMLDivElement>) => {
    event?.stopPropagation();

    if (
      screen === ModalTypeEnum.Locations ||
      screen === ModalTypeEnum.Filters ||
      (locationsPopupIsOpen && screen === ModalTypeEnum.City)
    ) {
      setLocationsPopupIsOpen(false);
    }

    setCitiesPopupIsOpen(true);
  };

  const toggleHandlerLocations = async () => {
    const locations = await getCityLocations(values.city_id);

    if (screen === ModalTypeEnum.Locations || screen === ModalTypeEnum.City) {
      hideFilters();
    }
    if (screen === ModalTypeEnum.Filters) {
      setCitiesPopupIsOpen(false);
    }
    if ((locations?.length || 0) > 0) {
      setLocationsPopupIsOpen(!locationsPopupIsOpen);
    } else {
      setCitiesPopupIsOpen(true);
    }
  };

  const renderSubmitText = (additionalFilters?: {
    [key: string]: string[];
  }) => {
    const preparedSearchVariables = {
      ...searchVariables,
      filter: { ...searchVariables.filter, ...additionalFilters }
    };

    if (resultByNumber.hasAnyResult) {
      const resultCount = resultByNumber.result ? 1 : 0;
      return t('search.form.show_n_ads_one', {
        count: resultCount
      });
    }

    return (
      <Query
        ssr={false}
        variables={preparedSearchVariables}
        fetchPolicy="cache-and-network"
        query={searchTotalCountQuery}
      >
        {({
          loading,
          data,
          error
        }: QueryResult<SearchTotalCountQuery, OperationVariables>) => {
          if (error) {
            return t('search.form.show_ads');
          }

          const count = data?.itemsConnection?.totalCount || 0;

          return loading || submitting ? (
            <Loader />
          ) : (
            t('search.form.show_n_ads_many', { count })
          );
        }}
      </Query>
    );
  };

  return (
    <>
      <NewSearchFilters
        formEl={formEl}
        hide={hideFilters}
        isOpen={screen === ModalTypeEnum.Filters}
        undo={handleHideWithUndo}
        modalParams={modalParams}
        toggleHandlerLocations={toggleHandlerLocations}
        renderSubmitText={renderSubmitText}
      />
      <NewSearchCities
        citiesPopupIsOpen={citiesPopupIsOpen}
        closeCitiesHandler={closeCitiesHandler}
      />
      <NewSearchLocations
        modalParams={modalParams}
        formValues={values}
        locationsPopupIsOpen={locationsPopupIsOpen}
        toggleHandlerLocations={toggleHandlerLocations}
        openCitiesHandler={openCitiesHandler}
        isInitialScreen={modalParams?.initialScreen === ModalTypeEnum.Locations}
        renderSubmitText={renderSubmitText}
      />
    </>
  );
};
